import type {
  RectBounds,
  SpawnPoint,
  WalkObstacle,
} from '@/world-prototype/types';

export type WorldPoint = SpawnPoint;

/**
 * Axis-aligned bounds that enclose a polygon (for camera / validation helpers).
 */
export function boundsFromPolygon(polygon: readonly WorldPoint[]): RectBounds {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const p of polygon) {
    xMin = Math.min(xMin, p.x);
    xMax = Math.max(xMax, p.x);
    yMin = Math.min(yMin, p.y);
    yMax = Math.max(yMax, p.y);
  }
  return { xMin, xMax, yMin, yMax };
}

export function pointInPolygon(
  x: number,
  y: number,
  polygon: readonly WorldPoint[],
): boolean {
  if (polygon.length < 3) return false;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    if (pointOnSegment(x, y, a, b)) return true;
  }
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const intersect =
      a.y > y !== b.y > y &&
      x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y + Number.EPSILON) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function pointInBounds(x: number, y: number, bounds: RectBounds): boolean {
  return (
    x >= bounds.xMin &&
    x <= bounds.xMax &&
    y >= bounds.yMin &&
    y <= bounds.yMax
  );
}

export function clampToBounds(x: number, y: number, bounds: RectBounds): WorldPoint {
  return {
    x: clamp(x, bounds.xMin, bounds.xMax),
    y: clamp(y, bounds.yMin, bounds.yMax),
  };
}

/** Nearest point on a polygon edge; identity if already inside. */
export function clampToPolygon(
  x: number,
  y: number,
  polygon: readonly WorldPoint[],
): WorldPoint {
  if (polygon.length < 3) return { x, y };
  if (pointInPolygon(x, y, polygon)) return { x, y };

  return nearestPointOnPolygonEdge(x, y, polygon);
}

/**
 * If inside an obstacle footprint, push to the nearest point on its edge.
 * Outer walk bounds are unchanged — this only carves solid interiors.
 */
export function pushOutOfObstacles(
  x: number,
  y: number,
  obstacles: readonly WalkObstacle[] | undefined,
): WorldPoint {
  if (!obstacles || obstacles.length === 0) return { x, y };

  let px = x;
  let py = y;
  // Two passes cover nested / adjacent footprints without looping forever.
  for (let pass = 0; pass < 2; pass += 1) {
    let hit: WalkObstacle | null = null;
    for (const obstacle of obstacles) {
      if (
        obstacle.polygon.length >= 3 &&
        pointInPolygon(px, py, obstacle.polygon)
      ) {
        hit = obstacle;
        break;
      }
    }
    if (!hit) break;
    const edge = nearestPointOnPolygonEdge(px, py, hit.polygon);
    // Nudge slightly outward from the obstacle centroid so we don't stick on the edge.
    const c = polygonCentroid(hit.polygon);
    const dx = edge.x - c.x;
    const dy = edge.y - c.y;
    const len = Math.hypot(dx, dy) || 1;
    px = edge.x + (dx / len) * 0.002;
    py = edge.y + (dy / len) * 0.002;
  }
  return { x: px, y: py };
}

export function pointInAnyObstacle(
  x: number,
  y: number,
  obstacles: readonly WalkObstacle[] | undefined,
): boolean {
  if (!obstacles) return false;
  return obstacles.some(
    (o) => o.polygon.length >= 3 && pointInPolygon(x, y, o.polygon),
  );
}

/**
 * Route from start to target that walks around obstacle footprints.
 *
 * Straight-line movement re-aims at the target every frame, so a footprint
 * directly between character and target stalls the walk. Instead, plan the
 * route once: when the direct segment crosses an obstacle, run Dijkstra over
 * {start, target, inflated obstacle vertices}, where a hop is valid only if
 * it avoids every obstacle interior and stays inside the walk polygon.
 *
 * Returns the waypoints to visit (target included, start excluded).
 * Falls back to [target] when nothing blocks or no route exists.
 */
export function planWalkRoute(
  start: WorldPoint,
  target: WorldPoint,
  walkPolygon: readonly WorldPoint[] | undefined,
  obstacles: readonly WalkObstacle[] | undefined,
  margin = 0.012,
): WorldPoint[] {
  const solid = (obstacles ?? []).filter((o) => o.polygon.length >= 3);
  if (
    solid.length === 0 ||
    !segmentCrossesAnyObstacle(start, target, solid)
  ) {
    return [target];
  }

  // Via nodes: obstacle vertices pushed outward, kept on walkable ground.
  const nodes: WorldPoint[] = [start, target];
  for (const obstacle of solid) {
    for (const vertex of inflatePolygon(obstacle.polygon, margin)) {
      let p: WorldPoint = vertex;
      if (walkPolygon && walkPolygon.length >= 3) {
        p = clampToPolygon(p.x, p.y, walkPolygon);
      }
      p = pushOutOfObstacles(p.x, p.y, solid);
      nodes.push(p);
    }
  }

  const canHop = (a: WorldPoint, b: WorldPoint): boolean => {
    if (segmentCrossesAnyObstacle(a, b, solid)) return false;
    if (walkPolygon && walkPolygon.length >= 3) {
      if (!segmentInsidePolygon(a, b, walkPolygon)) return false;
    }
    return true;
  };

  // Dijkstra over the tiny node graph.
  const n = nodes.length;
  const dist = new Array<number>(n).fill(Infinity);
  const prev = new Array<number>(n).fill(-1);
  const done = new Array<boolean>(n).fill(false);
  dist[0] = 0;
  for (let iter = 0; iter < n; iter += 1) {
    let u = -1;
    for (let i = 0; i < n; i += 1) {
      if (!done[i] && (u === -1 || dist[i]! < dist[u]!)) u = i;
    }
    if (u === -1 || dist[u] === Infinity) break;
    done[u] = true;
    if (u === 1) break;
    for (let v = 0; v < n; v += 1) {
      if (done[v] || v === u) continue;
      if (!canHop(nodes[u]!, nodes[v]!)) continue;
      const d =
        dist[u]! + Math.hypot(nodes[v]!.x - nodes[u]!.x, nodes[v]!.y - nodes[u]!.y);
      if (d < dist[v]!) {
        dist[v] = d;
        prev[v] = u;
      }
    }
  }

  if (dist[1] === Infinity) return [target];
  const route: WorldPoint[] = [];
  for (let at = 1; at !== 0; at = prev[at]!) {
    route.unshift(nodes[at]!);
    if (prev[at] === -1) break;
  }
  return route.length > 0 ? route : [target];
}

/** Vertices pushed outward from the polygon centroid by `margin`. */
export function inflatePolygon(
  polygon: readonly WorldPoint[],
  margin: number,
): WorldPoint[] {
  const c = polygonCentroid(polygon);
  return polygon.map((p) => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: p.x + (dx / len) * margin, y: p.y + (dy / len) * margin };
  });
}

function segmentCrossesAnyObstacle(
  a: WorldPoint,
  b: WorldPoint,
  obstacles: readonly WalkObstacle[],
): boolean {
  // Shrink slightly so endpoints resting on a boundary don't false-positive.
  const sa = lerpPoint(a, b, 0.002);
  const sb = lerpPoint(b, a, 0.002);
  for (const obstacle of obstacles) {
    const poly = obstacle.polygon;
    if (pointInPolygon((sa.x + sb.x) / 2, (sa.y + sb.y) / 2, poly)) return true;
    for (let i = 0; i < poly.length; i += 1) {
      if (segmentsIntersect(sa, sb, poly[i]!, poly[(i + 1) % poly.length]!)) {
        return true;
      }
    }
  }
  return false;
}

/** Both endpoints inside and no boundary crossing → segment stays inside. */
function segmentInsidePolygon(
  a: WorldPoint,
  b: WorldPoint,
  polygon: readonly WorldPoint[],
): boolean {
  const sa = lerpPoint(a, b, 0.002);
  const sb = lerpPoint(b, a, 0.002);
  if (!pointInPolygon(sa.x, sa.y, polygon)) return false;
  if (!pointInPolygon(sb.x, sb.y, polygon)) return false;
  for (let i = 0; i < polygon.length; i += 1) {
    if (
      segmentsIntersect(sa, sb, polygon[i]!, polygon[(i + 1) % polygon.length]!)
    ) {
      return false;
    }
  }
  return true;
}

function segmentsIntersect(
  a: WorldPoint,
  b: WorldPoint,
  c: WorldPoint,
  d: WorldPoint,
): boolean {
  const d1 = orient(c, d, a);
  const d2 = orient(c, d, b);
  const d3 = orient(a, b, c);
  const d4 = orient(a, b, d);
  if (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  ) {
    return true;
  }
  return false;
}

function orient(a: WorldPoint, b: WorldPoint, c: WorldPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function lerpPoint(a: WorldPoint, b: WorldPoint, t: number): WorldPoint {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function nearestPointOnPolygonEdge(
  x: number,
  y: number,
  polygon: readonly WorldPoint[],
): WorldPoint {
  let bestX = polygon[0]!.x;
  let bestY = polygon[0]!.y;
  let bestDist = Infinity;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    const projected = projectPointToSegment(x, y, a, b);
    const dist = Math.hypot(projected.x - x, projected.y - y);
    if (dist < bestDist) {
      bestDist = dist;
      bestX = projected.x;
      bestY = projected.y;
    }
  }

  return { x: bestX, y: bestY };
}

function polygonCentroid(polygon: readonly WorldPoint[]): WorldPoint {
  let sx = 0;
  let sy = 0;
  for (const p of polygon) {
    sx += p.x;
    sy += p.y;
  }
  const n = polygon.length || 1;
  return { x: sx / n, y: sy / n };
}

function projectPointToSegment(
  px: number,
  py: number,
  a: WorldPoint,
  b: WorldPoint,
): WorldPoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq <= Number.EPSILON) return { x: a.x, y: a.y };
  const t = clamp(((px - a.x) * dx + (py - a.y) * dy) / lenSq, 0, 1);
  return { x: a.x + dx * t, y: a.y + dy * t };
}

function pointOnSegment(
  px: number,
  py: number,
  a: WorldPoint,
  b: WorldPoint,
  epsilon = 1e-6,
): boolean {
  const projected = projectPointToSegment(px, py, a, b);
  return Math.hypot(projected.x - px, projected.y - py) <= epsilon;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
